import { Emitter } from "@core/events/emitter";
import type { EntityData, EntitySnapshot } from "@models/data/entityData";
import { normalizeExplosionFactor } from "@models/data/explosionFactor";
import type { IProjectModel } from "@models/project/IProjectModel";
import type {
  ExplosionChangedEvent,
  IExplosionModifierModel,
} from "./IExplosionModifierModel";

interface ExplosionTarget {
  readonly entity: EntityData;
  readonly root: EntitySnapshot;
  readonly key: string;
}

/**
 * Headless port of Unity's `IExplosionModifierModel`.
 *
 * Three.js hierarchy deformation is owned by the scene renderer. This model
 * owns factor state, target resolution, and root-snapshot persistence.
 */
export class ExplosionModifierModel implements IExplosionModifierModel {
  readonly onExplosionChanged = new Emitter<ExplosionChangedEvent>();

  private readonly m_factors = new Map<string, number>();
  private readonly m_offFns: Array<() => void> = [];

  constructor(private readonly m_project: IProjectModel) {
    this.m_offFns.push(
      this.m_project.onEntityChangesApplied.on(({ sceneId }) => {
        this.syncScene(sceneId);
      }),
      this.m_project.onActiveProjectChanged.on((project) => {
        if (!project) this.m_factors.clear();
      }),
    );
  }

  getExplosionFactor(sceneId: string, snapshotId: string): number {
    const target = this.resolveTarget(sceneId, snapshotId);
    if (!target) return normalizeExplosionFactor(undefined);
    return this.m_factors.get(target.key)
      ?? normalizeExplosionFactor(target.root.explosionFactor);
  }

  getRootSnapshotId(sceneId: string, snapshotId: string): string | null {
    return this.resolveTarget(sceneId, snapshotId)?.root.id ?? null;
  }

  canExplode(sceneId: string, snapshotId: string): boolean {
    const target = this.resolveTarget(sceneId, snapshotId);
    if (!target || target.root.isLocked) return false;
    return countExplodableSnapshots(target.root) > 1;
  }

  setExplosionFactor(
    sceneId: string,
    snapshotId: string,
    factor: number,
  ): number | null {
    const target = this.resolveTarget(sceneId, snapshotId);
    if (!target) return null;
    const normalized = normalizeExplosionFactor(factor);
    const oldFactor = this.getExplosionFactor(sceneId, target.root.id);
    if (oldFactor === normalized) return normalized;

    this.m_factors.set(target.key, normalized);
    this.onExplosionChanged.emit({
      sceneId,
      entityId: target.entity.id,
      rootSnapshotId: target.root.id,
      oldFactor,
      newFactor: normalized,
    });
    return normalized;
  }

  async commitExplosionFactorAsync(
    sceneId: string,
    snapshotId: string,
    factor?: number,
  ): Promise<number | null> {
    const target = this.resolveTarget(sceneId, snapshotId);
    if (!target) return null;
    const committed =
      factor === undefined
        ? this.getExplosionFactor(sceneId, target.root.id)
        : this.setExplosionFactor(sceneId, target.root.id, factor);
    if (committed === null) return null;

    await this.m_project.updateEntitySnapshotAsync(sceneId, target.root.id, {
      explosionFactor: committed,
    });
    return committed;
  }

  dispose(): void {
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
    this.m_factors.clear();
    this.onExplosionChanged.clear();
  }

  private syncScene(sceneId: string): void {
    const validKeys = new Set<string>();
    for (const entity of this.m_project.getEntities(sceneId)) {
      for (const root of entity.snapshots.filter((snapshot) => snapshot.isRoot)) {
        const target = this.targetFor(sceneId, entity, root);
        validKeys.add(target.key);
        const persisted = normalizeExplosionFactor(root.explosionFactor);
        const current = this.m_factors.get(target.key);
        if (current === undefined) continue;
        if (current === persisted) continue;
        this.m_factors.set(target.key, persisted);
        this.onExplosionChanged.emit({
          sceneId,
          entityId: entity.id,
          rootSnapshotId: root.id,
          oldFactor: current,
          newFactor: persisted,
        });
      }
    }
    for (const key of this.m_factors.keys()) {
      if (key.startsWith(`${sceneId}|`) && !validKeys.has(key)) {
        this.m_factors.delete(key);
      }
    }
  }

  private resolveTarget(
    sceneId: string,
    snapshotId: string,
  ): ExplosionTarget | null {
    const entity = this.m_project.getEntityBySnapshotId(sceneId, snapshotId);
    if (!entity) return null;
    const root =
      entity.snapshots.find((candidate) => containsSnapshot(candidate, snapshotId))
      ?? entity.snapshots.find((candidate) => candidate.isRoot)
      ?? entity.snapshots[0];
    return root ? this.targetFor(sceneId, entity, root) : null;
  }

  private targetFor(
    sceneId: string,
    entity: EntityData,
    root: EntitySnapshot,
  ): ExplosionTarget {
    return {
      entity,
      root,
      key: `${sceneId}|${entity.id}|${root.id}`,
    };
  }
}

function containsSnapshot(root: EntitySnapshot, snapshotId: string): boolean {
  if (root.id === snapshotId) return true;
  return root.children.some((child) => containsSnapshot(child, snapshotId));
}

function countExplodableSnapshots(root: EntitySnapshot): number {
  let count = root.isRoot || root.name === "Origin" ? 0 : 1;
  for (const child of root.children) {
    count += countExplodableSnapshots(child);
  }
  return count;
}
